import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, Plus, Minus, Send } from "lucide-react";
import api, { apiErr } from "@/lib/api";

export default function SupplyShop() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get("/supply/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/supply/orders/mine").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const setQty = (id, d) => setCart((c) => { const q = Math.max(0, (c[id] || 0) + d); const n = { ...c }; if (q === 0) delete n[id]; else n[id] = q; return n; });
  const total = useMemo(() => products.reduce((s, p) => s + (cart[p.id] || 0) * p.price, 0), [cart, products]);
  const categories = [...new Set(products.map((p) => p.category))];

  const submit = async () => {
    const items = Object.entries(cart).map(([product_id, quantity]) => ({ product_id, quantity }));
    if (!items.length) { toast.error("Votre panier est vide"); return; }
    setBusy(true);
    try { await api.post("/supply/orders", { items }); toast.success("Commande PLV envoyée à la startup !"); setCart({}); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {categories.map((cat) => (
          <div key={cat}>
            <h3 className="font-display text-2xl cc-navy mb-3">{cat}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {products.filter((p) => p.category === cat).map((p) => (
                <div key={p.id} data-testid={`supply-product-${p.id}`} className="card-cocarde p-4 flex items-center justify-between">
                  <div>
                    <p className="font-bold cc-navy">{p.name}</p>
                    <p className="text-slate-500 text-sm">{p.price} € · {p.unit}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`supply-minus-${p.id}`} onClick={() => setQty(p.id, -1)} className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center"><Minus size={14} /></button>
                    <span className="w-6 text-center font-bold" data-testid={`supply-qty-${p.id}`}>{cart[p.id] || 0}</span>
                    <button data-testid={`supply-plus-${p.id}`} onClick={() => setQty(p.id, 1)} className="w-8 h-8 rounded-full bg-cc-red text-white grid place-items-center"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <div className="card-cocarde p-5 sticky top-20">
          <h3 className="font-display text-2xl cc-navy flex items-center gap-2"><ShoppingCart size={20} /> Panier</h3>
          <div className="mt-3 space-y-1 text-sm">
            {Object.entries(cart).map(([id, q]) => { const p = products.find((x) => x.id === id); return p ? (
              <div key={id} className="flex justify-between text-slate-600"><span>{q}× {p.name}</span><span className="font-bold">{(q * p.price).toFixed(2)} €</span></div>
            ) : null; })}
            {!Object.keys(cart).length && <p className="text-slate-400">Panier vide</p>}
          </div>
          <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100">
            <span className="font-display text-lg cc-navy">TOTAL</span>
            <span className="font-display text-2xl text-cc-red" data-testid="supply-total">{total.toFixed(2)} €</span>
          </div>
          <button data-testid="supply-submit" onClick={submit} disabled={busy} className="btn-cocarde w-full mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Send size={16} /> {busy ? "Envoi…" : "Commander à la startup"}
          </button>
        </div>

        <div className="card-cocarde p-5">
          <h4 className="font-display text-lg cc-navy mb-2">Mes commandes PLV</h4>
          {history.map((o) => (
            <div key={o.id} className="flex justify-between text-sm py-1.5 border-b border-slate-50">
              <span className="text-slate-500">{o.items.length} article(s) · {o.total} €</span>
              <span className="font-bold text-cc-blue capitalize">{o.status}</span>
            </div>
          ))}
          {!history.length && <p className="text-slate-400 text-sm">Aucune commande.</p>}
        </div>
      </div>
    </div>
  );
}
